import * as React from 'react';
import React__default, { memo, useMemo, useState, useEffect } from 'react';
import classnames from 'classnames';
import { Icon } from '@grandvisionhq/icons';
import * as swcHelpers from '@swc/helpers';
import { Html5QrcodeScannerState, Html5Qrcode } from 'html5-qrcode';
export { Html5QrcodeSupportedFormats as SupportedCodeFormats } from 'html5-qrcode';
import { motion, AnimatePresence } from 'framer-motion';
import debounce from 'lodash/debounce';

function Article({ children  }) {
    return /*#__PURE__*/ React.createElement("article", {
        className: "article"
    }, children);
}

var SkeletonLoader = (_param)=> {
    var { animate =true , className , children , 'data-t': dataT = 'skeleton-loader'  } = _param, rest = swcHelpers.objectWithoutProperties(_param, [
        "animate",
        "className",
        "children",
        'data-t'
    ]);
    return React.createElement("div", swcHelpers.extends({
        className: classnames('skeleton-loader', {
            'skeleton-loader--animated': animate
        }, className),
        "data-t": dataT,
        "aria-busy": true
    }, rest), children);
};

const Display = (_param)=>{
    var { align ='inherit' , color ='inherit' , className , component ='h1' , 'data-t': testId = 'typography-display'  } = _param, rest = swcHelpers.objectWithoutProperties(_param, [
        "align",
        "color",
        "className",
        "component",
        'data-t'
    ]);
    const Component = component || 'h1';
    return /*#__PURE__*/ React.createElement(Component, swcHelpers.extends({
        className: classnames('typography-display', className, {
            [`typography-display--align-${align}`]: align !== 'inherit',
            [`typography-display--color-${color}`]: color !== 'inherit'
        }),
        "data-t": testId
    }, rest));
};

const variantMap$1 = {
    large: 'h1',
    medium: 'h2',
    small: 'h3',
    smallest: 'h4',
    subheading1: 'h5',
    subheading2: 'h6'
};
const Heading = (_param)=>{
    var { align ='inherit' , className , color ='inherit' , component , visualHeadingOnly =false , variant ='large' , 'data-t': testId = 'typography-heading'  } = _param, rest = swcHelpers.objectWithoutProperties(_param, [
        "align",
        "className",
        "color",
        "component",
        "visualHeadingOnly",
        "variant",
        'data-t'
    ]);
    const mappedVariant = variantMap$1[variant];
    const Component = component || mappedVariant;
    const hasAria = component === 'div' && !visualHeadingOnly;
    return /*#__PURE__*/ React.createElement(Component, swcHelpers.extends({}, hasAria && {
        role: 'heading',
        'aria-level': mappedVariant ? parseInt(mappedVariant.substring(1), 10) : 2
    }, {
        className: classnames('typography-heading', `typography-heading--${variant}`, className, {
            [`typography-heading--align-${align}`]: align !== 'inherit',
            [`typography-heading--color-${color}`]: color !== 'inherit'
        }),
        "data-t": testId
    }, rest));
};

const variantMap = {
    body: 'p',
    'body-head': 'strong',
    caption: 'small',
    'caption-head': 'strong',
    link: 'span',
    list: 'span'
};
const Text = (_param)=>{
    var { align ='inherit' , className , color ='inherit' , component , variant ='body' , 'data-t': testId = 'typography-text'  } = _param, rest = swcHelpers.objectWithoutProperties(_param, [
        "align",
        "className",
        "color",
        "component",
        "variant",
        'data-t'
    ]);
    const Component = component || variantMap[variant];
    return /*#__PURE__*/ React.createElement(Component, swcHelpers.extends({
        className: classnames('typography-text', `typography-text--${variant}`, className, {
            [`typography-text--align-${align}`]: align !== 'inherit',
            [`typography-text--color-${color}`]: color !== 'inherit'
        }),
        "data-t": testId
    }, rest));
};

/**
 * Scans both barcodes and qrcodes
 */ const CodeScannerCamera = ({ cameraError , cameraContainerRef , labels , className , status ,  })=>{
    return /*#__PURE__*/ React.createElement("div", {
        className: classnames('code-scanner', className)
    }, /*#__PURE__*/ React.createElement("div", {
        ref: cameraContainerRef,
        className: `code-scanner__camera code-scanner__camera--${status}`
    }), status !== 'error' && status !== 'scanning' && /*#__PURE__*/ React.createElement(SkeletonLoader, {
        className: "code-scanner__loader"
    }, /*#__PURE__*/ React.createElement("svg", {
        fill: "none",
        xmlns: "http://www.w3.org/2000/svg",
        "aria-label": labels.loadingText
    }, /*#__PURE__*/ React.createElement("rect", {
        width: "100%",
        height: "100%",
        rx: "5",
        fill: "currentColor"
    }))), status === 'scanning' && /*#__PURE__*/ React.createElement("div", {
        className: "code-scanner__description"
    }, /*#__PURE__*/ React.createElement(Text, {
        className: "code-scanner__text",
        variant: "body-head"
    }, labels.title), /*#__PURE__*/ React.createElement("div", {
        className: "code-scanner__spacer"
    }), /*#__PURE__*/ React.createElement(Text, {
        className: "code-scanner__text"
    }, labels.description)), status === 'error' && /*#__PURE__*/ React.createElement(ErrorScreen, {
        className: "code-scanner__error-screen",
        title: labels[`${cameraError}ErrorTitle`],
        description: labels[`${cameraError}ErrorDescription`]
    }));
};
const ErrorScreen = ({ className , title , description  })=>/*#__PURE__*/ React.createElement("div", {
        className: className
    }, /*#__PURE__*/ React.createElement(Icon, {
        className: `${className}__content`,
        name: "warning",
        height: 32,
        width: 32
    }), /*#__PURE__*/ React.createElement(Heading, {
        className: `${className}__content`,
        variant: "subheading1",
        component: "h2"
    }, title), /*#__PURE__*/ React.createElement(Text, {
        className: `${className}__content`
    }, description))
;

const initialState = {
    status: 'uninit',
    cameraError: null
};
function createRandomId(prefix) {
    return `${prefix}${Math.random().toString(16).substring(2)}`;
}
const useCodeScannerCamera = ({ onCodeDetected , onError , cameraContainerRef , formatsToSupport  })=>{
    const scannerRef = React.useRef(null);
    const cameraContainerId = React.useMemo(()=>createRandomId('scanner-')
    , []);
    const [{ cameraError , status  }, setState] = useStateReducer(swcHelpers.objectSpread({}, initialState));
    const stopScanner = React.useCallback(swcHelpers.asyncToGenerator(function*() {
        var ref;
        const scanningState = (ref = scannerRef.current) === null || ref === void 0 ? void 0 : ref.getState();
        if (scanningState === Html5QrcodeScannerState.SCANNING) {
            try {
                var ref1;
                yield (ref1 = scannerRef.current) === null || ref1 === void 0 ? void 0 : ref1.stop();
            } catch (e) {
                console.error(e);
            }
        }
        setState({
            status: 'stopped'
        });
    }), [
        setState
    ]);
    const onScanSuccess = React.useCallback((decodedText)=>{
        onCodeDetected(decodedText);
        stopScanner();
    }, [
        onCodeDetected,
        stopScanner
    ]);
    const handleScannerError = React.useCallback((err)=>{
        console.debug('Code Scanner Error:', err);
        setState({
            status: 'error',
            cameraError: err.toLowerCase().includes('permission denied') ? 'permissionDenied' : 'other'
        });
        onError && onError(err);
    }, [
        onError,
        setState
    ]);
    const getAspectRatio = React.useCallback((container)=>{
        const height = container.offsetHeight;
        const width = container.offsetWidth;
        return width / height;
    }, []);
    const getQrBoxSize = React.useCallback(()=>{
        var ref;
        const height = 336;
        let width = 288 //default size
        ;
        var ref2;
        // Check if the width is larger than our Tablet breakpoint, 768
        const containerWidth = (ref2 = (ref = cameraContainerRef.current) === null || ref === void 0 ? void 0 : ref.offsetWidth) !== null && ref2 !== void 0 ? ref2 : 0;
        containerWidth > 768 ? width = 488 : width;
        return {
            height,
            width
        };
    }, [
        cameraContainerRef
    ]);
    const initScanner = React.useCallback(swcHelpers.asyncToGenerator(function*() {
        var ref;
        setState({
            status: 'loading'
        });
        let cameras = [];
        try {
            cameras = yield Html5Qrcode.getCameras();
        } catch (e) {
            handleScannerError(typeof e === 'string' ? e : 'other');
        }
        if (!(cameras === null || cameras === void 0 ? void 0 : cameras.length)) return;
        cameraContainerRef === null || cameraContainerRef === void 0 ? void 0 : (ref = cameraContainerRef.current) === null || ref === void 0 ? void 0 : ref.setAttribute('id', cameraContainerId);
        scannerRef.current = new Html5Qrcode(cameraContainerId, swcHelpers.objectSpread({
            verbose: false
        }, !!(formatsToSupport === null || formatsToSupport === void 0 ? void 0 : formatsToSupport.length) ? {
            formatsToSupport
        } : {}));
        setState({
            status: 'ready'
        });
    }), [
        setState,
        handleScannerError,
        cameraContainerRef,
        cameraContainerId,
        formatsToSupport
    ]);
    const startScanning = React.useCallback(swcHelpers.asyncToGenerator(function*() {
        if (!(scannerRef === null || scannerRef === void 0 ? void 0 : scannerRef.current) || status !== 'ready' || !cameraContainerRef.current) {
            setState({
                status: 'error',
                cameraError: 'other'
            });
            return;
        }
        const aspectRatio = getAspectRatio(cameraContainerRef.current);
        const qrbox = getQrBoxSize();
        try {
            yield scannerRef.current.start({
                facingMode: 'environment'
            }, {
                aspectRatio,
                fps: 10,
                qrbox
            }, onScanSuccess, undefined // Do nothing for unsuccessful scans
            );
        } catch (e) {
            handleScannerError(typeof e === 'string' ? e : 'other');
        }
        setState({
            status: 'scanning'
        });
    }), [
        status,
        cameraContainerRef,
        getAspectRatio,
        getQrBoxSize,
        onScanSuccess,
        handleScannerError,
        setState, 
    ]);
    React.useEffect(()=>{
        if (status === 'ready') {
            startScanning();
        }
    }, [
        startScanning,
        status
    ]);
    return {
        initScanner,
        stopScanner,
        status,
        cameraError
    };
};
function useStateReducer(initialState1) {
    return React.useReducer((state, action)=>swcHelpers.objectSpread({}, state, action)
    , initialState1);
}

const posedIcon = {
    buttonWithDropdownIdle: {
        rotate: 0
    },
    buttonWithDropdownIsActive: {
        rotate: 180
    }
};
const posedContent = {
    enter: {
        opacity: 1,
        y: 0,
        overflow: 'hidden',
        transitionEnd: {
            overflow: ''
        }
    },
    exit: {
        opacity: 0,
        y: -25,
        overflow: 'hidden',
        transition: {
            duration: 0
        }
    }
};
class ButtonWithDropdown extends React__default.Component {
    componentDidMount() {
        window.addEventListener('resize', this.handleResize);
        this.setOffset();
    }
    componentWillUnmount() {
        window.removeEventListener('resize', this.handleResize);
    }
    componentDidUpdate(prevProps, prevState) {
        if (prevProps.isActive !== this.props.isActive || prevState.isActive !== this.state.isActive) {
            this.setOffset();
        }
    }
    render() {
        const _props = this.props, { label , children , isFullWidth , title , isDisabled , className  } = _props, rest = swcHelpers.objectWithoutProperties(_props, [
            "label",
            "children",
            "isFullWidth",
            "title",
            "isDisabled",
            "className"
        ]);
        const { offset  } = this.state;
        const isControlled = typeof this.props.isActive === 'boolean';
        const isActive = isControlled ? this.props.isActive : this.state.isActive;
        const dropdownClassnames = classnames('button-with-dropdown', {
            _is_full_width: isFullWidth
        });
        const buttonClassnames = classnames('button-with-dropdown__button', className, {
            _is_active: isActive,
            _is_full_width: !!isFullWidth
        });
        const contentClassnames = classnames('button-with-dropdown__content', {
            _is_active: isActive,
            _is_full_width: !!isFullWidth,
            _is_disabled: isDisabled
        });
        return /*#__PURE__*/ React__default.createElement(motion.div, {
            className: dropdownClassnames,
            animate: isActive ? 'buttonWithDropdownIsActive' : 'buttonWithDropdownIdle'
        }, /*#__PURE__*/ React__default.createElement("button", {
            className: buttonClassnames,
            onClick: this.handleClick,
            type: "button",
            "aria-haspopup": "true",
            "aria-expanded": isActive,
            "aria-label": rest['aria-label'] || title,
            "aria-labelledby": rest['aria-labelledby'],
            "aria-describedby": rest['aria-describedby']
        }, /*#__PURE__*/ React__default.createElement("span", {
            className: "button-with-dropdown__button-inner"
        }, /*#__PURE__*/ React__default.createElement("span", {
            className: "button-with-dropdown__label"
        }, label), !isDisabled && /*#__PURE__*/ React__default.createElement(motion.span, {
            className: "button-with-dropdown__icon",
            variants: posedIcon
        }, /*#__PURE__*/ React__default.createElement(Icon, {
            name: "chevron-down"
        })))), /*#__PURE__*/ React__default.createElement("div", {
            className: "button-with-dropdown__content-container",
            style: {
                left: offset
            }
        }, /*#__PURE__*/ React__default.createElement(AnimatePresence, null, isActive && /*#__PURE__*/ React__default.createElement(motion.span, {
            initial: "exit",
            animate: "enter",
            exit: "exit",
            variants: posedContent,
            key: "_",
            className: contentClassnames,
            ref: this.contentRef
        }, children))));
    }
    constructor(...args){
        super(...args);
        this.state = {
            offset: 0,
            isActive: typeof this.props.isActive === 'boolean' ? this.props.isActive : this.props.initialActive || false
        };
        this.contentRef = /*#__PURE__*/ React__default.createRef();
        this.handleResize = debounce(()=>this.setOffset()
        , 60);
        this.setOffset = ()=>{
            if (this.contentRef.current && !this.props.isFullWidth) {
                const { innerWidth  } = window;
                const { right  } = this.contentRef.current.getBoundingClientRect();
                const scrollBarWidth = 25 // IE needs this
                ;
                const offset = Math.min(innerWidth - (right - this.state.offset + scrollBarWidth), 0);
                if (offset !== this.state.offset) {
                    this.setState({
                        offset
                    });
                }
            }
        };
        this.handleClick = ()=>{
            const isControlled = typeof this.props.isActive === 'boolean';
            const isActive = !(isControlled ? this.props.isActive : this.state.isActive);
            !isControlled && this.setState(()=>({
                    isActive
                })
            );
            this.props.onClick && this.props.onClick(isActive);
        };
    }
}

/**
 *  Checkbox component without label and error message
 *
 *  @deprecated
 *  Checkbox will be removed when all consumers have moved to
 *  elements/CheckboxWithLabel component
 *
 */ class Checkbox extends (React.PureComponent) {
    render() {
        const _props = this.props, { id , checked , disabled , error  } = _props, rest = swcHelpers.objectWithoutProperties(_props, [
            "id",
            "checked",
            "disabled",
            "error"
        ]);
        return /*#__PURE__*/ React.createElement("label", {
            className: classnames('checkbox', [
                this.props.className,
                error && !checked ? 'checkbox__error' : '', 
            ])
        }, /*#__PURE__*/ React.createElement("input", {
            className: "checkbox__input",
            type: "checkbox",
            id: id,
            "aria-checked": checked,
            "aria-label": rest['aria-label'],
            "aria-invalid": error && !checked,
            checked: typeof checked === 'boolean' ? checked : false,
            disabled: disabled,
            onChange: disabled ? undefined : this.onClick,
            "data-t": this.props['data-t'],
            tabIndex: 0,
            ref: this.inputRef,
            value: "0" // Not really used anywhere but it's a required attribute
        }), /*#__PURE__*/ React.createElement("span", {
            className: "checkbox__mask"
        }), checked === 'mixed' ? /*#__PURE__*/ React.createElement("span", {
            className: "checkbox__indeterminate"
        }) : /*#__PURE__*/ React.createElement("svg", {
            className: "checkbox__checkmark",
            width: "14",
            height: "13",
            viewBox: "0 0 14 13",
            fill: "none",
            xmlns: "http://www.w3.org/2000/svg",
            "aria-hidden": true
        }, /*#__PURE__*/ React.createElement("path", {
            d: "M1 7.5L4.5 12L13 1",
            fill: "none"
        })));
    }
    componentDidUpdate(prevProps) {
        if (this.inputRef.current) {
            this.inputRef.current.indeterminate = this.props.checked === 'mixed';
        }
    }
    constructor(...args){
        super(...args);
        this.inputRef = /*#__PURE__*/ React.createRef();
        this.onClick = (e)=>{
            this.props.onChange({
                value: this.props.value,
                checked: e.currentTarget.checked
            });
        };
    }
}
Checkbox.defaultProps = {
    checked: false,
    disabled: false
};

const Container = ({ children , className  })=>/*#__PURE__*/ React.createElement("div", {
        className: classnames('container', className)
    }, children)
;

var Divider = (_param)=>{
    var { className , orientation ='horizontal' , theme ='regular' , isFlexItem =false , caption  } = _param, rest = swcHelpers.objectWithoutProperties(_param, [
        "className",
        "orientation",
        "theme",
        "isFlexItem",
        "caption"
    ]);
    const hasWrapper = orientation !== 'vertical';
    const divider = /*#__PURE__*/ React.createElement("hr", swcHelpers.extends({
        className: classnames('divider', `divider--${orientation}`, {
            'divider--flex-item': isFlexItem,
            'divider--light': theme === 'light',
            'divider--caption': caption
        }, !hasWrapper && className),
        "aria-orientation": orientation
    }, !hasWrapper && rest));
    if (!hasWrapper) {
        return divider;
    }
    return /*#__PURE__*/ React.createElement("div", swcHelpers.extends({
        className: classnames('divider-wrapper', className, {
            'divider-wrapper--caption': caption
        })
    }, rest), divider, caption && /*#__PURE__*/ React.createElement("span", {
        className: "divider__caption"
    }, caption));
};

const Hr = ({ className  })=>/*#__PURE__*/ React.createElement("hr", {
        className: classnames('horizontal-rule', className)
    })
;

const Label = ({ type , variant ='medium' , className , children  })=>/*#__PURE__*/ React.createElement("b", {
        className: classnames('label', `label--${type}`, `label--${variant}`, className)
    }, children)
;

const Tag = ({ children , onClick  })=>{
    return /*#__PURE__*/ React.createElement("button", {
        className: "tag",
        onClick: onClick,
        type: "button"
    }, children, /*#__PURE__*/ React.createElement(Icon, {
        className: "tag__clear",
        name: "clear"
    }));
};

const TextArea = (_param)=>{
    var { className , disabled , maxLength , onChange , onBlur , placeholder , name , id  } = _param, rest = swcHelpers.objectWithoutProperties(_param, [
        "className",
        "disabled",
        "maxLength",
        "onChange",
        "onBlur",
        "placeholder",
        "name",
        "id"
    ]);
    const resolvedId = id || name;
    const handleChange = React__default.useCallback((e)=>{
        !disabled && onChange && onChange(e.currentTarget.value);
    }, [
        disabled,
        onChange
    ]);
    const handleBlur = React__default.useCallback((e)=>{
        !disabled && onBlur && onBlur(e.currentTarget.value);
    }, [
        disabled,
        onBlur
    ]);
    return /*#__PURE__*/ React__default.createElement("textarea", swcHelpers.extends({}, rest, {
        name: name,
        id: resolvedId,
        className: classnames('textarea', className),
        disabled: disabled,
        maxLength: maxLength,
        onChange: handleChange,
        onBlur: handleBlur,
        placeholder: placeholder
    }));
};

const ResponsiveImage = ({ src , srcSet , alt , className , sizes , 'data-t': dataT ,  })=>/*#__PURE__*/ React.createElement("img", {
        "data-t": dataT || 'responsive-image',
        width: "100%",
        className: classnames('responsive-image', className),
        src: src,
        srcSet: srcSet,
        sizes: sizes,
        alt: alt
    })
;

const ButtonLoader = /*#__PURE__*/ memo(()=>/*#__PURE__*/ React__default.createElement("div", {
        "aria-hidden": "true",
        className: "button--loader"
    })
);

const Button = (_param)=>{
    var { children , className , type ='button' , variant , size , loading , loadingLabel  } = _param, props = swcHelpers.objectWithoutProperties(_param, [
        "children",
        "className",
        "type",
        "variant",
        "size",
        "loading",
        "loadingLabel"
    ]);
    const classes = useMemo(()=>classnames('btn', `btn--${variant}`, {
            [`btn--${size}`]: variant !== 'ghost' && size && size !== 'medium'
        }, className)
    , [
        className,
        variant,
        size
    ]);
    const a11y = useMemo(()=>{
        if (!loading) {
            return {};
        }
        return swcHelpers.objectSpread({
            role: 'status',
            'aria-live': 'polite'
        }, loadingLabel && {
            'aria-label': loadingLabel
        });
    }, [
        loading,
        loadingLabel
    ]);
    return /*#__PURE__*/ React__default.createElement("button", swcHelpers.extends({
        type: type,
        className: classes
    }, props, a11y), loading && /*#__PURE__*/ React__default.createElement(ButtonLoader, null), /*#__PURE__*/ React__default.createElement("span", null, children));
};

const ToggleButton = (_param)=>{
    var { value , selected , type ='checkbox' , onChange  } = _param, props = swcHelpers.objectWithoutProperties(_param, [
        "value",
        "selected",
        "type",
        "onChange"
    ]);
    const [active, setActive] = useState(selected);
    function clickHandler() {
        if (active && type === 'radio') {
            return;
        }
        setActive(!active);
        if (onChange) {
            onChange(!active ? value : '');
        }
    }
    useEffect(()=>{
        setActive(selected);
    }, [
        selected
    ]);
    return /*#__PURE__*/ React__default.createElement(Button, swcHelpers.extends({
        value: active ? value : undefined,
        onClick: clickHandler,
        variant: active ? 'solid' : 'outline'
    }, props));
};

function optionsToValues(options) {
    return options.reduce((acc, opt)=>[
            ...acc,
            opt.selected ? opt.value : ''
        ]
    , []).filter((value)=>Boolean(value)
    );
}
const ButtonGroup = (_param)=>{
    var { className , type ='checkbox' , size , block , options , onChange  } = _param, props = swcHelpers.objectWithoutProperties(_param, [
        "className",
        "type",
        "size",
        "block",
        "options",
        "onChange"
    ]);
    const classes = classnames('btn-group', {
        [`btn-group--${size}`]: size && size !== 'medium'
    }, {
        'btn-group--block': block
    }, className);
    const [values, setValues] = useState(options);
    useEffect(()=>{
        setValues(options);
    }, [
        options
    ]);
    const handleChange = (index, value)=>{
        let newOptions;
        switch(type){
            case 'radio':
                newOptions = values.map((option, mapIndex)=>{
                    return swcHelpers.objectSpread({}, option, {
                        selected: index === mapIndex
                    });
                });
                setValues(newOptions);
                if (onChange) {
                    const result = optionsToValues(newOptions)[0];
                    onChange(result);
                }
                break;
            case 'checkbox':
                newOptions = values.map((option, mapIndex)=>{
                    if (index === mapIndex) {
                        return swcHelpers.objectSpread({}, option, {
                            selected: option.value === value
                        });
                    }
                    return swcHelpers.objectSpread({}, option);
                });
                setValues(newOptions);
                if (onChange) {
                    const result = optionsToValues(newOptions);
                    onChange(result);
                }
                break;
        }
    };
    const a11y = useMemo(()=>type === 'radio' ? {
            role: 'radiogroup',
            buttonRole: 'radio'
        } : {
            role: 'group',
            buttonRole: 'checkbox'
        }
    , [
        type
    ]);
    return /*#__PURE__*/ React__default.createElement("div", swcHelpers.extends({
        className: classes
    }, props, {
        role: a11y.role
    }), values.map((opt, index)=>/*#__PURE__*/ React__default.createElement(ToggleButton, {
            role: a11y.buttonRole,
            type: type,
            size: size,
            value: opt.value,
            selected: opt.selected,
            "aria-checked": opt.selected,
            key: opt.value.toString(),
            onChange: (val)=>handleChange(index, val)
        }, opt.label)
    ));
};

const RoundedButton = (_param)=>{
    var { children , className , type ='button' , variant , size , loading , loadingLabel  } = _param, props = swcHelpers.objectWithoutProperties(_param, [
        "children",
        "className",
        "type",
        "variant",
        "size",
        "loading",
        "loadingLabel"
    ]);
    const classes = useMemo(()=>classnames('rounded-button', `rounded-button--${variant}`, {
            [`rounded-button--${size}`]: size && size !== 'medium'
        }, className)
    , [
        className,
        variant,
        size
    ]);
    const a11y = useMemo(()=>{
        if (!loading) {
            return {};
        }
        return swcHelpers.objectSpread({
            role: 'status',
            'aria-live': 'polite'
        }, loadingLabel && {
            'aria-label': loadingLabel
        });
    }, [
        loading,
        loadingLabel
    ]);
    return /*#__PURE__*/ React__default.createElement("button", swcHelpers.extends({
        type: type,
        className: classes
    }, props, a11y), loading ? /*#__PURE__*/ React__default.createElement(ButtonLoader, null) : children);
};

var Radio = (_param)=>{
    var { children , className  } = _param, htmlInputProps = swcHelpers.objectWithoutProperties(_param, [
        "children",
        "className"
    ]);
    return /*#__PURE__*/ React__default.createElement("label", {
        className: classnames('radio', className)
    }, /*#__PURE__*/ React__default.createElement("input", swcHelpers.extends({
        type: "radio",
        className: "radio__input"
    }, htmlInputProps)), children);
};

const Toggle = (_param)=>{
    var { id , className , checked: isChecked , disabled =false , onChange  } = _param, rest = swcHelpers.objectWithoutProperties(_param, [
        "id",
        "className",
        "checked",
        "disabled",
        "onChange"
    ]);
    const [checked, setChecked] = React.useState(isChecked);
    const handleOnChange = React.useCallback((e)=>{
        onChange(e.currentTarget.checked);
        setChecked(e.currentTarget.checked);
    }, [
        onChange
    ]);
    const handleKeyPress = React.useCallback((e)=>{
        if ((e.key === 'Enter' || e.key === 'Space') && !disabled) {
            onChange(!checked);
            setChecked(!checked);
        }
    }, [
        checked,
        disabled,
        onChange
    ]);
    return /*#__PURE__*/ React.createElement("label", swcHelpers.extends({}, rest, {
        className: classnames('toggle', className)
    }), /*#__PURE__*/ React.createElement("input", {
        id: id,
        className: "toggle__input",
        type: "checkbox",
        checked: checked,
        disabled: disabled,
        "aria-checked": checked,
        "aria-label": rest['aria-label'],
        onChange: handleOnChange,
        onKeyUp: handleKeyPress
    }), /*#__PURE__*/ React.createElement("span", {
        className: "toggle__slider"
    }));
};

const Details = (_param)=>{
    var { title , subtitle , className , children , summaryAttributes  } = _param, rest = swcHelpers.objectWithoutProperties(_param, [
        "title",
        "subtitle",
        "className",
        "children",
        "summaryAttributes"
    ]);
    const [open, setOpen] = React.useState(false);
    return /*#__PURE__*/ React.createElement("details", swcHelpers.extends({}, rest, {
        open: open,
        className: classnames('details', className),
        onToggle: ()=>setOpen(!open)
    }), /*#__PURE__*/ React.createElement("summary", swcHelpers.extends({}, summaryAttributes, {
        className: "details__summary"
    }), /*#__PURE__*/ React.createElement("span", {
        className: "details__icon"
    }, /*#__PURE__*/ React.createElement(Icon, {
        width: 18,
        height: 18,
        name: open ? 'minus' : 'plus'
    })), /*#__PURE__*/ React.createElement(Text, {
        variant: "body-head",
        color: "primary"
    }, title), subtitle && /*#__PURE__*/ React.createElement(Text, {
        variant: "caption",
        color: "text-secondary"
    }, "(", subtitle, ")")), /*#__PURE__*/ React.createElement("div", {
        className: "details__content"
    }, children));
};

const getSelectionBoxStyles = ({ isSelected , isDisabled , className  } = {})=>classnames('selection-box', className, {
        _is_selected: isSelected && !isDisabled,
        _is_disabled: isDisabled
    })
;

export { Article, Button, ButtonGroup, ButtonWithDropdown, Checkbox, CodeScannerCamera, Container, Details, Display, Divider, Heading, Hr, Label, Radio, ResponsiveImage, RoundedButton, SkeletonLoader, Tag, Text, TextArea, Toggle, ToggleButton, getSelectionBoxStyles, useCodeScannerCamera, variantMap$1 as variantMap };
//# sourceMappingURL=index.js.map
